import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';
import {
  CondensedMedium,
  Flashy,
  Icon,
  Layout,
  PluralizeUtil,
} from '@axiom/ui';
import type { Candidate, CandidateOpportunity } from '@axiom/validation';
import { TaxonomyConst } from '@axiom/const';

import { CandidateUtil } from '../../utils/candidate-util';

const { LawyerBusinessTitle } = TaxonomyConst;

interface TalentProfileWarningsProps {
  candidate: Candidate;
  onAvailabilityClick?: (candidateId: Candidate['id']) => void;
  onOpportunitiesClick?: () => void;
}

interface WarningItem {
  id: string;
  isMatched: boolean;
  children: ReactNode;
  link?: {
    label: string;
    onClick: () => void;
  };
}

export const TalentProfileWarnings: FC<TalentProfileWarningsProps> = ({
  candidate,
  onAvailabilityClick,
  onOpportunitiesClick,
}) => {
  const warningItems = useMemo(() => {
    const items: WarningItem[] = [];

    // 1. Hours of availability warning
    const weeklyAvailability = candidate.weeklyAvailability ?? 0;
    const roundedHours = Math.round(weeklyAvailability);
    const hoursText = `${roundedHours} ${PluralizeUtil({ text: 'hr', count: roundedHours })} available`;

    items.push({
      id: 'warning-hours-availability',
      isMatched: weeklyAvailability > 0,
      children: hoursText,
      link: onAvailabilityClick
        ? {
            label: 'View calculation',
            onClick: () => {
              onAvailabilityClick(candidate.id);
            },
          }
        : undefined,
    });

    // 2. Cross-fulfilled warning
    const crossFulfillCounts = CandidateUtil.getCrossFulfillmentCounts(
      candidate.candidateOpportunities as unknown as CandidateOpportunity[]
    );
    const crossFulfillMessages =
      CandidateUtil.formatCrossFulfillmentCountsMessage(crossFulfillCounts);

    items.push({
      id: 'warning-cross-fulfilled',
      isMatched: !candidate.isCrossFulfilled,
      children: (
        <>
          Cross:{' '}
          {crossFulfillMessages.length > 0
            ? crossFulfillMessages.join(', ')
            : 'Not cross-fulfilled'}
        </>
      ),
      link: onOpportunitiesClick
        ? {
            label: 'View opps',
            onClick: onOpportunitiesClick,
          }
        : undefined,
    });

    // 3. Not admitted in home state warning
    // Only show for Lawyers in the US
    if (
      candidate.businessTitle === LawyerBusinessTitle &&
      candidate.addressCountryCode === 'US' &&
      candidate.addressState
    ) {
      const isAdmittedInHomeState = CandidateUtil.isBarredInState(
        candidate.barredLocations,
        candidate.addressState
      );

      if (!isAdmittedInHomeState) {
        const stateType = CandidateUtil.isGreenState(candidate.addressState)
          ? 'green'
          : 'red';
        items.push({
          id: 'warning-not-admitted-home-state',
          isMatched: false,
          children: `Not admitted to work in home state of ${candidate.addressState} (${stateType})`,
        });
      }
    }

    // 4. Minimum pay / hours per position warning
    const minHoursPerPosition = candidate.minWeeklyHoursPerPosition ?? 0;
    if (minHoursPerPosition > 0) {
      const hoursWord = PluralizeUtil({
        text: 'hour',
        count: minHoursPerPosition,
      });
      items.push({
        id: 'warning-min-hours-per-position',
        isMatched: false,
        children: `Cannot be engaged for less than ${minHoursPerPosition} ${hoursWord} per position`,
      });
    }

    return items;
  }, [candidate, onAvailabilityClick, onOpportunitiesClick]);

  if (warningItems.length === 0) {
    return null;
  }

  return (
    <Layout
      name="talent-profile-warnings"
      direction="vertical"
      verticalGutter="4px"
    >
      {warningItems.map(item => (
        <Layout
          key={item.id}
          horizontalGutter="8px"
          position="left middle"
          name={item.id}
        >
          <CondensedMedium name="warning-item-icon">
            <Flashy
              color={item.isMatched ? 'dataVizGreen' : 'dataVizYellow'}
              name={`warning-item-${item.isMatched ? 'matched' : 'unmatched'}`}
            >
              <Layout position="middle">
                <Icon
                  name={item.isMatched ? 'circle-check-filled' : 'alert-filled'}
                />
              </Layout>
            </Flashy>
          </CondensedMedium>
          <CondensedMedium>
            <span data-test={`${item.id}-text`}>{item.children}</span>{' '}
            {item.link ? (
              <a
                data-test={`${item.id}-link`}
                href="#"
                onClick={e => {
                  e.preventDefault();
                  item.link?.onClick();
                }}
              >
                {item.link.label}
              </a>
            ) : null}
          </CondensedMedium>
        </Layout>
      ))}
    </Layout>
  );
};
